// Market Scanner Component - Live market view with USL analysis

import { useState, useEffect } from 'react';
import { KalshiMarket } from '../lib/types';
import { useTradingStore, getDaysUntilExpiration } from '../lib/store';
import { analyzeMarket } from '../lib/usl';

// Format days until expiration for display
const formatDaysUntil = (market: KalshiMarket): string => {
  const days = getDaysUntilExpiration(market);
  if (days === null) return '—';
  if (days < 0) return 'Expired';
  if (days === 0) return 'Today!';
  if (days === 1) return '1 day';
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.ceil(days / 7)}w`;
  if (days < 365) return `${Math.ceil(days / 30)}mo`;
  return `${(days / 365).toFixed(1)}yr`;
};

// Color code by urgency
const getClosesColor = (market: KalshiMarket): string => {
  const days = getDaysUntilExpiration(market);
  if (days === null) return '#666';
  if (days < 0) return '#e74c3c';   // Red - expired
  if (days <= 1) return '#e74c3c';  // Red - today/tomorrow
  if (days <= 7) return '#2ecc71';  // Green - this week
  if (days <= 30) return '#f1c40f'; // Yellow - this month
  if (days <= 90) return '#fff';    // White - this quarter
  return '#888';                     // Gray - far out
};

export function MarketScanner() {
  const markets = useTradingStore((state) => state.markets);
  const qualifyingA = useTradingStore((state) => state.qualifyingMarketsA);
  const qualifyingB = useTradingStore((state) => state.qualifyingMarketsB);
  const lastUpdate = useTradingStore((state) => state.lastUpdate);
  const refreshMarkets = useTradingStore((state) => state.refreshMarkets);
  const executeTrade = useTradingStore((state) => state.executeTrade);
  const config = useTradingStore((state) => state.config);
  const setConfig = useTradingStore((state) => state.setConfig);
  const isRunning = useTradingStore((state) => state.isRunning);
  
  const [activeTab, setActiveTab] = useState<'A' | 'B' | 'all'>('B');
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshMarkets();
    setIsRefreshing(false);
  };

  // Re-fetch when date filter changes
  useEffect(() => {
    if (isRunning && markets.length > 0) {
      refreshMarkets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.maxDaysToClose]);
  
  const displayMarkets = activeTab === 'A' ? qualifyingA 
    : activeTab === 'B' ? qualifyingB 
    : markets.slice(0, 50);

  return (
    <div className="market-scanner">
      <div className="scanner-header">
        <h3>🔍 Market Scanner</h3>
        <div className="scanner-controls">
          <div className="tab-buttons">
            <button 
              className={`tab-btn ${activeTab === 'B' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveTab('B');
              }}
            >
              Strategy B ({qualifyingB.length})
            </button>
            <button 
              className={`tab-btn ${activeTab === 'A' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveTab('A');
              }}
            >
              Strategy A ({qualifyingA.length})
            </button>
            <button 
              className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveTab('all');
              }}
            >
              All ({markets.length})
            </button>
          </div>
          
          {/* DATE FILTER DROPDOWN */}
          <div className="date-filter">
            <span>Closes:</span>
            <select 
              value={config.maxDaysToClose}
              onChange={(e) => setConfig({ maxDaysToClose: parseInt(e.target.value) })}
            >
              <option value={7}>1 week</option>
              <option value={30}>1 month</option>
              <option value={90}>3 months</option>
              <option value={180}>6 months</option>
              <option value={365}>1 year</option>
              <option value={9999}>All time</option>
            </select>
          </div>
          
          <button 
            className="btn-refresh"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRefresh();
            }}
            disabled={isRefreshing}
          >
            {isRefreshing ? '⏳' : '🔄'} Refresh
          </button>
        </div>
      </div>
      
      {lastUpdate && (
        <div className="last-update">
          Last update: {lastUpdate.toLocaleTimeString()} | 
          Showing markets closing within {config.maxDaysToClose < 9999 ? `${config.maxDaysToClose} days` : 'any time'}
        </div>
      )}
      
      <div className="market-list">
        {displayMarkets.length === 0 ? (
          <div className="empty-state">
            <p>No qualifying markets found within {config.maxDaysToClose} days.</p>
            <p>Try increasing the date range or click Refresh.</p>
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRefresh();
              }}
            >
              Load Markets
            </button>
          </div>
        ) : (
          <table className="market-table">
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Price</th>
                <th>Volume</th>
                <th>Closes</th>
                <th>USL Residual</th>
                <th>Signal</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayMarkets.map((market) => (
                <MarketRow 
                  key={market.ticker}
                  market={market}
                  activeStrategy={activeTab}
                  onTrade={(strategy) => executeTrade(strategy, market)}
                  canTrade={isRunning}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

interface MarketRowProps {
  market: KalshiMarket;
  activeStrategy: 'A' | 'B' | 'all';
  onTrade: (strategy: 'A' | 'B') => void;
  canTrade: boolean;
}

function MarketRow({ market, activeStrategy, onTrade, canTrade }: MarketRowProps) {
  const analysis = analyzeMarket(market);
  
  const residualClass = analysis.uslResidual > 0.1 ? 'positive' 
    : analysis.uslResidual < -0.1 ? 'negative' 
    : 'neutral';

  // Check if market qualifies for each strategy
  const qualifiesA = analysis.uslResidual > 0.1 && 
                     market.volume >= 100 && 
                     market.volume <= 1000 &&
                     market.last_price > 0 &&
                     market.last_price < 100;
                     
  const qualifiesB = market.last_price >= 20 && 
                     market.last_price <= 40 &&
                     market.volume >= 100 && 
                     market.volume <= 1000;
  
  return (
    <tr className="market-row">
      <td className="ticker">
        <div className="ticker-info">
          <span className="ticker-name">{market.ticker}</span>
          <span className="ticker-title" title={market.title}>
            {market.title && market.title.length > 30 ? market.title.slice(0, 30) + '...' : market.title}
          </span>
        </div>
      </td>
      <td className="price">{market.last_price}¢</td>
      <td className="volume">{market.volume?.toLocaleString()}</td>
      <td 
        className="closes" 
        style={{ 
          color: getClosesColor(market), 
          fontWeight: getDaysUntilExpiration(market) !== null && getDaysUntilExpiration(market)! <= 7 ? 'bold' : 'normal' 
        }}
      >
        {formatDaysUntil(market)}
      </td>
      <td className={`residual ${residualClass}`}>
        {analysis.uslResidual > 0 ? '+' : ''}{(analysis.uslResidual * 100).toFixed(1)}%
      </td>
      <td className="signal">
        <span className={`signal-badge ${analysis.signal.toLowerCase().replace('_', '-')}`}>
          {analysis.signal}
        </span>
      </td>
      <td className="actions">
        {canTrade ? (
          <>
            {(activeStrategy === 'A' || activeStrategy === 'all') && qualifiesA && (
              <button 
                className="btn-trade btn-trade-a"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onTrade('A');
                }}
                title="Strategy A: Bet AGAINST market"
              >
                A
              </button>
            )}
            {(activeStrategy === 'B' || activeStrategy === 'all') && qualifiesB && (
              <button 
                className="btn-trade btn-trade-b"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onTrade('B');
                }}
                title="Strategy B: Bet WITH market"
              >
                B
              </button>
            )}
          </>
        ) : (
          <span style={{ color: '#666', fontSize: '0.75rem' }}>—</span>
        )}
      </td>
    </tr>
  );
}
